import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { 
  Users, 
  ChevronRight,
  CheckCircle2,
  Zap,
  Shield,
  Menu,
  X,
  CreditCard,
  Sparkles,
  Code,
  QrCode,
  UserCheck,
  Calendar,
  Mail,
  FileText,
  Link2,
  Key,
  Globe,
  Bell,
  RefreshCw,
  Tag,
  BarChart3,
  Settings,
  Webhook,
  BookOpen,
  Heart
} from 'lucide-react';

const HelpPage = ({ isOpen, onClose, initialSection = null }) => {
  const [activeSection, setActiveSection] = useState(initialSection || 'welcome');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Update active section when initialSection prop changes
  useEffect(() => {
    if (initialSection && initialSection !== activeSection) {
      setActiveSection(initialSection);
    }
  }, [initialSection, activeSection]);

  const sections = {
    welcome: {
      title: 'Welcome to BookingFlows',
      icon: Heart,
      content: (
        <div className="space-y-5">
          <div className="text-center py-4">
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 mb-3 shadow-lg">
              <Zap className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">BookingFlows</h2>
            <p className="text-gray-600">Make booking seamless for your industry</p>
          </div>

          <div className="p-6 border border-gray-200 rounded-xl" style={{backgroundColor: '#EDEFEB'}}>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">We make booking as easy and seamless as possible</h3>
            <p className="text-gray-700 leading-relaxed">
              BookingFlows is the perfect booking software for companies small or large. We handle the complexity so you can focus on your business.
            </p>
          </div>

          <div className="mt-6 mb-3">
            <h3 className="text-lg font-bold text-gray-900 mb-1">What Makes Us Different</h3>
            <p className="text-gray-600 text-sm">Built for real booking operations</p>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                  <Code className="w-5 h-5" />
                </div>
                <span className="font-semibold text-gray-900">Embeddable Widgets</span>
              </div>
              <p className="text-sm text-gray-600">Add booking to your website with payment processing</p>
            </div>

            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-50 text-green-600">
                  <QrCode className="w-5 h-5" />
                </div>
                <span className="font-semibold text-gray-900">4-Way Check-In</span>
              </div>
              <p className="text-sm text-gray-600">QR code, phone scan, manual, or customer self-service</p>
            </div>

            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
                  <Shield className="w-5 h-5" />
                </div>
                <span className="font-semibold text-gray-900">100% White Label</span>
              </div>
              <p className="text-sm text-gray-600">Your brand everywhere - no BookingFlows branding</p>
            </div>

            <div className="p-4 bg-white border border-gray-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-50 text-orange-600">
                  <CreditCard className="w-5 h-5" />
                </div>
                <span className="font-semibold text-gray-900">Direct Payments</span>
              </div>
              <p className="text-sm text-gray-600">Money goes straight to your Stripe account</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mt-4">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Browse the sidebar to learn about all our features - from automated emails to API integrations!
            </p>
          </div>
        </div>
      )
    },
    overview: {
      title: 'Platform Overview',
      icon: Zap,
      content: (
        <div className="space-y-5">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">How It Works</h3>
            <p className="text-sm text-gray-600 mb-3">Set up in 4 steps, then start accepting bookings</p>
            <div className="space-y-2">
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-semibold text-xs">1</div>
                <div className="text-sm">
                  <span className="font-semibold">Setup Team & Resources</span> - Define roles, add staff, register assets
                </div>
              </div>
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-green-50 text-green-600 rounded-full flex items-center justify-center font-semibold text-xs">2</div>
                <div className="text-sm">
                  <span className="font-semibold">Create Services</span> - Build what customers can book
                </div>
              </div>
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center font-semibold text-xs">3</div>
                <div className="text-sm">
                  <span className="font-semibold">Embed Widget</span> - Add to your website or share link
                </div>
              </div>
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-orange-50 text-orange-600 rounded-full flex items-center justify-center font-semibold text-xs">4</div>
                <div className="text-sm">
                  <span className="font-semibold">Manage Operations</span> - Track bookings, check-ins, revenue
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    services: {
      title: 'Services & Booking',
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">Creating Services</h3>
            <p className="text-sm text-gray-600 mb-3">Build what customers can book - tours, rentals, classes, appointments</p>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Dynamic pricing by day/time</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Multiple duration options</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Day-specific availability</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Add-ons & upgrades (fixed or percentage)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Photo galleries</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>AI content generation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Custom questions for customers</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Capacity Modes</h4>
            <div className="space-y-2 text-sm">
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-semibold">Per-Ticket:</span> Sell individual tickets up to capacity (e.g., group tours)
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-semibold">Per-Asset:</span> Book entire assets exclusively (e.g., boat rentals)
              </div>
            </div>
          </div>
        </div>
      )
    },
    emails: {
      title: 'Automated Emails',
      icon: Mail,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Email Automation</h3>
            <p className="text-sm text-gray-600 mb-4">Fully automated email flows to keep customers informed</p>
            
            <div className="space-y-3">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <h4 className="font-semibold text-sm">Booking Confirmation</h4>
                </div>
                <p className="text-sm text-gray-600">Sent immediately after payment with all booking details and magic link to ticket</p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Bell className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold text-sm">24-Hour Reminder</h4>
                </div>
                <p className="text-sm text-gray-600">Automatic reminder sent 24 hours before the booking</p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-4 h-4 text-pink-600" />
                  <h4 className="font-semibold text-sm">Thank You + Review Request</h4>
                </div>
                <p className="text-sm text-gray-600">Sent after the trip with a link to leave a review</p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="w-4 h-4 text-orange-600" />
                  <h4 className="font-semibold text-sm">Reschedule/Cancellation</h4>
                </div>
                <p className="text-sm text-gray-600">Automatic notifications when bookings change</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Owner Notifications</h4>
            <p className="text-sm text-gray-700">You also receive emails when new bookings come in, so you never miss a sale!</p>
          </div>
        </div>
      )
    },
    waivers: {
      title: 'Waivers & Signatures',
      icon: FileText,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Digital Waivers</h3>
            <p className="text-sm text-gray-600 mb-4">Collect legally binding digital signatures from customers</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Create custom waiver templates</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Customers sign on their phone/tablet</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Capture IP address, timestamp, device info</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Version tracking for legal compliance</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Export signed waivers anytime</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Signature Data Captured</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>• Full legal name</p>
              <p>• Digital signature (drawn)</p>
              <p>• Timestamp (UTC)</p>
              <p>• IP address</p>
              <p>• Device/browser info</p>
              <p>• Waiver version signed</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-1">Path</h4>
            <p className="text-sm text-gray-600">Customers → Waivers → Create/Edit Templates</p>
          </div>
        </div>
      )
    },
    relations: {
      title: 'Relations & Customers',
      icon: Users,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Customer Management</h3>
            <p className="text-sm text-gray-600 mb-4">Complete customer history and relationship tracking</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>View all bookings by customer</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>See total spent and booking history</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Process refunds directly</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>View signed waivers</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Track identity variations (same email, different names)</span>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Processing Refunds</h4>
            <p className="text-sm text-gray-700">
              Refunds are handled directly in your Stripe Dashboard. Go to Payments → Find the transaction → Click Refund.
              Then come back here and mark the booking as refunded to keep your records in sync.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-1">Path</h4>
            <p className="text-sm text-gray-600">Customers → Relations</p>
          </div>
        </div>
      )
    },
    checkIn: {
      title: 'Check-In System',
      icon: QrCode,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">4-Way Check-In</h3>
            <p className="text-sm text-gray-600 mb-4">Multiple ways to check customers in</p>
            
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded flex items-center justify-center text-xs font-bold">1</div>
                  <span className="font-semibold text-sm">QR Scanner</span>
                </div>
                <p className="text-sm text-gray-600">Use barcode gun to scan customer QR codes</p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 bg-green-100 text-green-600 rounded flex items-center justify-center text-xs font-bold">2</div>
                  <span className="font-semibold text-sm">Phone Camera</span>
                </div>
                <p className="text-sm text-gray-600">Scan QR with your phone&apos;s camera</p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded flex items-center justify-center text-xs font-bold">3</div>
                  <span className="font-semibold text-sm">Manual</span>
                </div>
                <p className="text-sm text-gray-600">Click check-in button in booking details</p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded flex items-center justify-center text-xs font-bold">4</div>
                  <span className="font-semibold text-sm">Customer Self-Service</span>
                </div>
                <p className="text-sm text-gray-600">Customer scans QR at your location</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Check-In Status Tracking</h4>
            <p className="text-sm text-gray-600">
              Track who&apos;s checked in, who&apos;s a no-show, and get real-time updates on your calendar and booking views.
            </p>
          </div>
        </div>
      )
    },
    promoCodes: {
      title: 'Promo Codes & Affiliates',
      icon: Tag,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Promo Codes</h3>
            <p className="text-sm text-gray-600 mb-4">Create discount codes for marketing and partnerships</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Percentage or fixed amount discounts</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Set maximum uses per code</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Set expiration dates</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Track usage and redemptions</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Affiliate Tracking</h4>
            <p className="text-sm text-gray-600 mb-3">
              Set up affiliate codes that track commissions. When someone books with an affiliate code, you can see who referred them and calculate payouts.
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Set commission percentages per affiliate</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Track referrals and earnings</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-1">Path</h4>
            <p className="text-sm text-gray-600">Financials → Promo Codes</p>
          </div>
        </div>
      )
    },
    calendar: {
      title: 'Calendar & Sync',
      icon: Calendar,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Calendar Features</h3>
            <p className="text-sm text-gray-600 mb-4">Full calendar management and external sync</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Day, week, and month views</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Filter by service, asset, or team member</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Drag and drop to reschedule</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Color-coded by service type</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Calendar Sync (iCal/Google)</h4>
            <p className="text-sm text-gray-700 mb-3">
              Subscribe to your BookingFlows calendar from Google Calendar, Apple Calendar, or any iCal-compatible app.
            </p>
            <div className="bg-white rounded p-2 text-sm">
              <span className="font-semibold">Path:</span> Settings → Calendar → Copy iCal URL → Add to your calendar app
            </div>
          </div>
        </div>
      )
    },
    apiKeys: {
      title: 'API Keys & Integrations',
      icon: Key,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Developer API</h3>
            <p className="text-sm text-gray-600 mb-4">Programmatic access to your BookingFlows data</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Generate secure API keys</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Manage bookings, services, customers</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Track API usage stats</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Revoke keys anytime</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">GetYourGuide & Viator Integration</h4>
            <p className="text-sm text-gray-600 mb-3">
              Connect your services to major OTAs (Online Travel Agencies) to expand your reach.
            </p>
            <div className="space-y-1 text-sm text-gray-600">
              <p>• Import bookings from GYG/Viator automatically</p>
              <p>• Sync availability in real-time</p>
              <p>• Track commission and revenue by channel</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-1">Path</h4>
            <p className="text-sm text-gray-600">Settings → API Keys</p>
          </div>
        </div>
      )
    },
    webhooks: {
      title: 'Webhooks',
      icon: Webhook,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Real-Time Webhooks</h3>
            <p className="text-sm text-gray-600 mb-4">Get notified instantly when events happen</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Booking created/updated/cancelled</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Payment received</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Customer check-in</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Customizable payload format</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Use Cases</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <p>• Update your CRM when new bookings arrive</p>
              <p>• Trigger custom automations in Zapier/Make</p>
              <p>• Sync with your accounting software</p>
              <p>• Send custom SMS via Twilio</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-1">Path</h4>
            <p className="text-sm text-gray-600">Settings → Webhooks</p>
          </div>
        </div>
      )
    },
    leads: {
      title: 'Leads & Cart Abandonment',
      icon: UserCheck,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Lead Capture</h3>
            <p className="text-sm text-gray-600 mb-4">Track potential customers who don&apos;t complete checkout</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Capture name and email from widget</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Track which service they were interested in</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>See where they dropped off</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Convert leads to customers</span>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Cart Abandonment</h4>
            <p className="text-sm text-gray-700">
              When someone enters their info in the widget but doesn&apos;t complete payment, they become a lead. 
              Follow up with them to recover the sale!
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-1">Path</h4>
            <p className="text-sm text-gray-600">Customers → Leads</p>
          </div>
        </div>
      )
    },
    payments: {
      title: 'Payments & Revenue',
      icon: CreditCard,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Stripe Integration</h3>
            <p className="text-sm text-gray-600 mb-4">Direct payments to your Stripe account</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Money goes directly to you (not through us)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Accept all major credit cards</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Apple Pay and Google Pay support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Refunds handled in your Stripe Dashboard</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Revenue Tracking</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <p>• Filter by date range, service, or asset</p>
              <p>• See actual vs projected revenue</p>
              <p>• Track refunds and cancellations</p>
              <p>• Export to CSV for accounting</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-sm mb-1">Path</h4>
            <p className="text-sm text-gray-600">Financials → Revenue</p>
          </div>
        </div>
      )
    },
    settings: {
      title: 'Settings & Config',
      icon: Settings,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Settings Overview</h3>
            <p className="text-sm text-gray-600 mb-4">Configure every aspect of your account</p>
            
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-sm mb-1">Profile</h4>
                <p className="text-sm text-gray-600">Business name, logo, contact info</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-sm mb-1">Branding</h4>
                <p className="text-sm text-gray-600">Colors, email templates, widget style</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-sm mb-1">Business Hours</h4>
                <p className="text-sm text-gray-600">Set your operating schedule</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-sm mb-1">Timezone</h4>
                <p className="text-sm text-gray-600">All bookings in your local time</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-sm mb-1">2FA Security</h4>
                <p className="text-sm text-gray-600">Add extra login security</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-sm mb-1">Activity Log</h4>
                <p className="text-sm text-gray-600">See all actions taken by your team</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  };

  const sidebarItems = [
    { id: 'welcome', label: 'Welcome', icon: Heart },
    { id: 'overview', label: 'How It Works', icon: Zap },
    { id: 'services', label: 'Services & Booking', icon: Sparkles },
    { id: 'emails', label: 'Automated Emails', icon: Mail },
    { id: 'waivers', label: 'Waivers & Signatures', icon: FileText },
    { id: 'relations', label: 'Relations & Customers', icon: Users },
    { id: 'checkIn', label: 'Check-In System', icon: QrCode },
    { id: 'promoCodes', label: 'Promo Codes & Affiliates', icon: Tag },
    { id: 'calendar', label: 'Calendar & Sync', icon: Calendar },
    { id: 'payments', label: 'Payments & Revenue', icon: CreditCard },
    { id: 'leads', label: 'Leads & Cart Abandonment', icon: UserCheck },
    { id: 'apiKeys', label: 'API Keys & Integrations', icon: Key },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'settings', label: 'Settings & Config', icon: Settings },
  ];

  const currentSection = sections[activeSection];
  const Icon = currentSection?.icon || Zap;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] h-[90vh] p-0 flex flex-col rounded-xl">
        <div className="flex flex-1 overflow-hidden min-h-0 rounded-xl">
          <div className={`${sidebarOpen ? 'w-64' : 'w-0'} md:w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto flex-shrink-0 transition-all duration-300 rounded-tl-2xl`}>
            <div className={`${sidebarOpen ? 'p-4' : 'p-0'} md:p-4`}>
              <DialogHeader className="mb-4">
                <DialogTitle className="text-base sm:text-lg">Help & Features Guide</DialogTitle>
              </DialogHeader>
              
              <nav className="space-y-1">
                {sidebarItems.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveSection(item.id);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === item.id
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <ItemIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                      {activeSection === item.id && (
                        <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-w-0 rounded-tr-2xl">
            <div className="md:hidden sticky top-0 bg-white border-b z-10 p-3">
              <Button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                variant="outline"
                size="sm"
                className="w-auto"
              >
                {sidebarOpen ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Menu className="w-4 h-4" />
                )}
              </Button>
            </div>

            <div className="p-4 sm:p-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                  <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <h2 className="text-lg sm:text-2xl font-bold">{currentSection?.title}</h2>
              </div>
              
              <div className="text-sm sm:text-base">
                {currentSection?.content}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpPage;
